import React, { useState, useEffect } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material"
import BluetoothIcon from "@mui/icons-material/Bluetooth"
import SearchIcon from "@mui/icons-material/Search"
import CloseIcon from "@mui/icons-material/Close"
import RestartAltIcon from "@mui/icons-material/RestartAlt"
import { useParams, useNavigate } from "react-router-dom"
import { useQuery, useQueryClient } from "react-query"
import BoardCanvas from "../components/BoardCanvas"
import RecentProblems, { slotBodyFor } from "../components/RecentProblems"
import { useHeaderSubtitle } from "../components/Layout"
import { useBoardConnection } from "../components/BoardConnection"
import { useGymFollow, GYM_POLL_INTERVAL_MS } from "../components/GymFollow"
import useStickyState from "../hooks/useStickyState"
import {
  fetchGym,
  createGym,
  clearGymSlot,
  setGymSlot,
  fetchGymHistory,
  setGymPalette,
  resetGymPalette,
  NotFoundError,
} from "../utils/gymApi"
import { COLOR_OPTIONS } from "../utils/bluetooth"
import {
  DEFAULT_PALETTES,
  HOLD_TYPES,
  buildPalette,
  paletteValuesForSlot,
  slotEntriesFor,
  toColorOption,
} from "../utils/gymDisplay"

const hasWebBluetooth =
  typeof navigator !== "undefined" && "bluetooth" in navigator

const gradeAtAngle = (climb, angle) => {
  if (!climb?.stats?.length) {
    return null
  }
  const stat =
    climb.stats.find((s) => s.angle === angle) || climb.stats[0]
  return `${stat.display_difficulty} @ ${stat.angle}°`
}

const dotSx = (rgb, selected) => ({
  width: 18,
  height: 18,
  borderRadius: "50%",
  p: 0,
  flexShrink: 0,
  cursor: "pointer",
  border: selected ? "2px solid" : "1px solid",
  borderColor: selected ? "text.primary" : "divider",
  bgcolor: `rgb(${rgb.join(",")})`,
})

// A hold-type color as a small swatch dot; tapping it opens the full
// 8x8 grid of wall colors
function ColorDotPicker({ label, value, onChange }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const current = COLOR_OPTIONS.find((option) => option.byte === value)
  return (
    <>
      <Tooltip title={`${label}: ${current?.name ?? value}`}>
        <Box
          component="button"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={dotSx(current?.rgb ?? [0, 0, 0], false)}
        />
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(8, 20px)",
            gap: 0.5,
            px: 1,
            py: 0.5,
          }}
        >
          {COLOR_OPTIONS.map(({ byte, name, rgb }) => (
            <Box
              key={byte}
              component="button"
              title={name}
              onClick={() => {
                onChange(byte)
                setAnchorEl(null)
              }}
              sx={dotSx(rgb, byte === value)}
            />
          ))}
        </Box>
      </Menu>
    </>
  )
}

function SlotCard({
  slug,
  slot,
  data,
  onStateChange,
  palette,
  paletteValues,
  onPaletteChange,
  onPaletteReset,
  holdStyle,
}) {
  const navigate = useNavigate()

  const handleClear = async () => {
    const state = await clearGymSlot(slug, slot)
    onStateChange(state)
  }

  const grade = data && gradeAtAngle(data.climb, data.angle)

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="caption"
            noWrap
            sx={{ fontWeight: 700, flexGrow: 1, minWidth: 0 }}
          >
            {data
              ? data.climb?.name || data.name || "Custom selection"
              : `Problem ${slot}`}
            {grade && (
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                {` · ${grade}`}
              </Typography>
            )}
          </Typography>
          <Tooltip title="Pick climb">
            <IconButton
              size="small"
              onClick={() =>
                navigate(`/search-climbs?gym=${slug}&slot=${slot}`)
              }
            >
              <SearchIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          {data && (
            <Tooltip title="Clear slot">
              <IconButton size="small" onClick={handleClear}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}
        >
          {HOLD_TYPES.map((type) => (
            <ColorDotPicker
              key={type}
              label={type.toLowerCase()}
              value={toColorOption(
                paletteValues[type],
                DEFAULT_PALETTES[slot][type]
              )}
              onChange={(byte) => onPaletteChange(type, byte)}
            />
          ))}
          <Tooltip title="Reset colors">
            <IconButton
              size="small"
              onClick={onPaletteReset}
              sx={{ ml: "auto" }}
            >
              <RestartAltIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
        {data ? (
          data.error === "climb_not_found" ? (
            <Typography color="error" variant="caption">
              This climb is no longer in the database.
            </Typography>
          ) : (
            <BoardCanvas
              climb={data.climb}
              palette={palette}
              holdStyle={holdStyle}
              maxHeight="40vh"
            />
          )
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "15vh",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Empty
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

function CreateGymPrompt({ slug, onCreated }) {
  const [name, setName] = useState("")
  const [isCreating, setIsCreating] = useState(false)

  const handleCreate = async () => {
    setIsCreating(true)
    try {
      const state = await createGym(slug, name || slug)
      onCreated(state)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Card variant="outlined" sx={{ maxWidth: 480, mx: "auto", mt: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          No gym here yet
        </Typography>
        <Typography color="text.secondary" gutterBottom>
          Create a page for the kilter board at <b>{slug}</b>? Anyone with
          this link will see what's currently on the wall.
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Display name (optional)"
          value={name}
          onChange={(event) => setName(event.target.value)}
          sx={{ my: 2 }}
        />
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={isCreating}
        >
          Create {slug}
        </Button>
      </CardContent>
    </Card>
  )
}

export default function Gym() {
  const { slug } = useParams()
  const queryClient = useQueryClient()
  const {
    characteristic: bluetoothCharacteristic,
    autoReconnect,
    setAutoReconnect,
  } = useBoardConnection()
  const { follow, sendGym, blankBoard } = useGymFollow()
  const [recentGyms, setRecentGyms] = useStickyState([], "recentGyms")
  const [holdStyle, setHoldStyle] = useStickyState("circle", "gymHoldStyle")
  const [tab, setTab] = useState("wall")
  const navigate = useNavigate()

  const { data: gym, error, isLoading } = useQuery(
    ["gym", slug],
    () => fetchGym(slug),
    {
      // Polling a missing gym would flip the query back to loading every
      // interval, remounting the create prompt and wiping its name field
      refetchInterval: (data, query) =>
        query.state.error instanceof NotFoundError
          ? false
          : GYM_POLL_INTERVAL_MS,
      refetchIntervalInBackground: true,
      retry: false,
    }
  )

  const setGymState = (state) => queryClient.setQueryData(["gym", slug], state)

  // Recently displayed problems refresh whenever a slot changes, which
  // polling surfaces on every client, not only the one that made the change
  const slotsKey = `${gym?.slots?.A?.updated_at}|${gym?.slots?.B?.updated_at}`
  const { data: history, isLoading: isHistoryLoading } = useQuery(
    ["gym-history", slug],
    () => fetchGymHistory(slug),
    { enabled: Boolean(gym), retry: false }
  )
  useEffect(() => {
    if (gym) {
      queryClient.invalidateQueries(["gym-history", slug])
    }
  }, [slotsKey])

  const redisplay = async (entry, slot) => {
    setGymState(await setGymSlot(slug, slot, slotBodyFor(entry)))
    setTab("wall")
  }

  // Colors are shared gym state: show the change at once, then persist it
  // so every other client (and the board, via whoever is connected) follows
  const changePalette = async (slot, type, value) => {
    const palette = { ...paletteValuesForSlot(gym.palettes, slot), [type]: value }
    setGymState({ ...gym, palettes: { ...gym.palettes, [slot]: palette } })
    setGymState(await setGymPalette(slug, slot, palette))
  }

  const resetPalette = async (slot) => {
    setGymState({ ...gym, palettes: { ...gym.palettes, [slot]: null } })
    setGymState(await resetGymPalette(slug, slot))
  }

  useHeaderSubtitle(gym?.name)

  useEffect(() => {
    if (!gym) {
      return
    }
    const entry = { slug: gym.slug, name: gym.name }
    setRecentGyms((recents) =>
      [entry, ...(recents || []).filter((g) => g.slug !== gym.slug)].slice(0, 5)
    )
    // Whichever gym page was opened last is the one a connected board
    // mirrors, even after navigating elsewhere
    follow(entry)
  }, [gym?.slug, gym?.name])

  const slotEntries = slotEntriesFor(gym)

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error instanceof NotFoundError) {
    return <CreateGymPrompt slug={slug} onCreated={setGymState} />
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ mt: 6 }}>
        Could not load this gym. Is the server running?
      </Typography>
    )
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          {bluetoothCharacteristic && (
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={<BluetoothIcon />}
                disabled={slotEntries.length === 0}
                onClick={() => sendGym(gym)}
              >
                Send to board
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                onClick={blankBoard}
              >
                Blank board
              </Button>
            </>
          )}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={holdStyle}
            onChange={(event, value) => value && setHoldStyle(value)}
            sx={{ display: { xs: "none", sm: "inline-flex" } }}
          >
            <ToggleButton value="circle">Circles</ToggleButton>
            <ToggleButton value="fill">Fill</ToggleButton>
          </ToggleButtonGroup>
          {hasWebBluetooth && (
            <Tooltip title="Reconnect to the board on its own if the link drops. Turn off if your board's Bluetooth misbehaves with repeated connections.">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={autoReconnect}
                    onChange={(event) => setAutoReconnect(event.target.checked)}
                  />
                }
                label="Auto-reconnect"
                slotProps={{ typography: { variant: "caption" } }}
                sx={{ ml: 0.5, mr: 0 }}
              />
            </Tooltip>
          )}
        </Box>
        {bluetoothCharacteristic && (
          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            display="block"
            sx={{ mb: 1 }}
          >
            This device mirrors the wall: anyone's changes to these problems
            are sent to the board automatically.
          </Typography>
        )}
        <Tabs
          value={tab}
          onChange={(event, value) => setTab(value)}
          centered
          sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0 } }}
        >
          <Tab label="On the wall" value="wall" />
          <Tab label="Recent" value="recent" />
        </Tabs>
      </Grid>
      {tab === "recent" && (
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
              <RecentProblems
                entries={history}
                isLoading={isHistoryLoading}
                onDisplay={redisplay}
                onOpen={(entry) => navigate(`/climb/${entry.climb_uuid}`)}
              />
            </CardContent>
          </Card>
        </Grid>
      )}
      {tab === "wall" && (
      <Grid item xs={12} md={6}>
        <SlotCard
          slug={slug}
          slot="A"
          data={gym.slots?.A}
          onStateChange={setGymState}
          palette={buildPalette(gym.palettes, "A")}
          paletteValues={paletteValuesForSlot(gym.palettes, "A")}
          holdStyle={holdStyle}
          onPaletteChange={(type, value) => changePalette("A", type, value)}
          onPaletteReset={() => resetPalette("A")}
        />
      </Grid>
      )}
      {tab === "wall" && (
      <Grid item xs={12} md={6}>
        <SlotCard
          slug={slug}
          slot="B"
          data={gym.slots?.B}
          onStateChange={setGymState}
          palette={buildPalette(gym.palettes, "B")}
          paletteValues={paletteValuesForSlot(gym.palettes, "B")}
          holdStyle={holdStyle}
          onPaletteChange={(type, value) => changePalette("B", type, value)}
          onPaletteReset={() => resetPalette("B")}
        />
      </Grid>
      )}
    </Grid>
  )
}
